<script setup lang="ts">
import { orderChipsBySections } from "@carnet/ui/utils/filterChips";
import type { UrlFilterValues } from "@/composables/useUrlFilters";

const appliedModel = defineModel<UrlFilterValues>("applied", { default: () => ({}) });

const { t } = useI18n();
const data = await useGlobalData();

const SECTION_KEYS = ["structureTypes", "structureDisciplines", "structureServices"] as const;
type SectionKey = (typeof SECTION_KEYS)[number];

const { applied, apply, clearDraft, clearAppliedKey, syncDraftFromApplied, toggleDraft, isDraftSelected } = useUrlFilters(
  () => SECTION_KEYS as readonly string[]
);

watch(applied, (v) => (appliedModel.value = v), { immediate: true });

const sections = computed(() => [
  { key: "structureTypes" as const, title: t("structures.filters.types.title"), items: data.value?.structureTypes ?? [] },
  { key: "structureDisciplines" as const, title: t("structures.filters.disciplines.title"), items: data.value?.structureDisciplines ?? [] },
  { key: "structureServices" as const, title: t("structures.filters.services.title"), items: data.value?.structureServices ?? [] },
]);

const accordionItems = computed(() =>
  sections.value.filter((s) => s.items.length > 0).map((s) => ({ value: s.key, label: s.title, slot: s.key as string }))
);

const open = ref(false);

watch(open, (isOpen) => {
  if (isOpen) syncDraftFromApplied();
});

function onApply() {
  apply();
  open.value = false;
}

function chipLabel(key: SectionKey, code: string): string {
  const list = (data.value?.[key] ?? []) as Array<{ code: string; label: string }>;
  return list.find((item) => item.code === code)?.label ?? code;
}

const chipEntries = computed(() =>
  orderChipsBySections(
    (Object.entries(applied.value) as [SectionKey, string[]][]).flatMap(([key, values]) =>
      values.map((value) => ({ key, value, label: chipLabel(key, value), removable: true }))
    ),
    sections.value.map((s) => s.key)
  )
);
</script>

<template>
  <WebFilters v-model:open="open" :empty="accordionItems.length === 0" @apply="onApply" @clear="clearDraft">
    <template #chips>
      <AppFilterChips :chips="chipEntries" @remove="clearAppliedKey" />
    </template>

    <UAccordion :items="accordionItems" type="multiple">
      <template v-for="section in sections" :key="section.key" #[`${section.key}-body`]>
        <ul class="flex flex-col">
          <li v-for="item in section.items" :key="item.code">
            <label class="flex items-start gap-3 py-2 text-base cursor-pointer select-none">
              <input
                type="checkbox"
                class="accent-blue-700 size-4 rounded-xs my-px"
                :checked="isDraftSelected(section.key, item.code)"
                @change="toggleDraft(section.key, item.code)"
              />
              <span>{{ item.label }}</span>
            </label>
          </li>
        </ul>
      </template>
    </UAccordion>
  </WebFilters>
</template>
